import type { EnvironmentId } from "@cmmd-forge/contracts";
import { Link } from "@tanstack/react-router";
import type { ReactElement } from "react";

import { cn } from "~/lib/utils";
import { ProjectIcon } from "../ProjectIcon";
import { BreadcrumbLeafActionButton } from "./BreadcrumbLeafActionButton";
import { BreadcrumbLeafRenameInput } from "./BreadcrumbLeafControls";
import type { BreadcrumbCrumb, BreadcrumbCrumbIcon } from "./globalBreadcrumb.logic";

/**
 * What the leaf crumb can do beyond naming the page.
 *
 * Optional and supplied from above. The bar is mounted over every route, so
 * this component must not learn to read the thread store itself — a select
 * here that builds a fresh object re-renders forever and blanks the whole app
 * (see the warning in `useGlobalBreadcrumbCrumbs`). The caller knows whether
 * the leaf is a thread; this only renders what it is handed.
 */
export interface BreadcrumbLeafActions {
  readonly onRename?: () => void;
  /** Right-click on the leaf. Absent on routes with no per-page menu. */
  readonly onContextMenu?: (position: { x: number; y: number }) => void;
  /**
   * The in-progress rename, when one is open.
   *
   * The leaf becomes an input rather than the label being replaced elsewhere:
   * renaming a thread from the bar should edit the name where it is read.
   */
  readonly rename?: {
    readonly value: string;
    readonly onCommit: (title: string) => void;
    readonly onCancel: () => void;
  };
}

interface GlobalBreadcrumbProps {
  readonly crumbs: readonly BreadcrumbCrumb[];
  readonly className?: string;
  readonly leafActions?: BreadcrumbLeafActions | undefined;
  /**
   * A qualifier rendered after the last crumb — the window a page is showing,
   * not another place you can be. Absent on every route that does not publish
   * one.
   */
  readonly detail?: string | undefined;
}

/**
 * The bar's identity: where you are, in one line.
 *
 * Ancestors are muted links, the leaf is the page. Below `sm` only the leaf
 * survives — a 320px bar cannot hold a path, and the crumb that answers "where
 * am I" is the last one, not the first.
 *
 * `flex-1` on the nav, not intrinsic width: the bar's left grid track is
 * `minmax(0, 1fr)`, so a nav sized by its content stops at its text and the
 * space the leaf needs in order to truncate late is handed back to the track.
 * Upstream sizes its breadcrumb the same way.
 *
 * Which is also why the nav must stay a drag surface. `flex-1` means it holds
 * whatever slack the bar has, and the bar is the frameless window's title bar —
 * a `no-drag-region` here made the empty half of every bar dead to dragging.
 * The crumbs that can be pressed are a `Link` (`<a>`), the leaf's action
 * `<button>`, and the rename `<input>`; `index.css` exempts all three inside
 * `.drag-region`. See the drag contract in `appTopBarLayout`.
 */
export function GlobalBreadcrumb({
  crumbs,
  className,
  leafActions,
  detail,
}: GlobalBreadcrumbProps): ReactElement | null {
  if (crumbs.length === 0) {
    return null;
  }

  const lastIndex = crumbs.length - 1;

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn("min-w-0 flex-1", className)}
      data-testid="global-breadcrumb"
    >
      <ol className="m-0 flex min-w-0 list-none items-center gap-1.5 p-0 text-sm">
        {crumbs.map((crumb, index) => (
          <GlobalBreadcrumbItem
            key={crumb.key}
            crumb={crumb}
            isLeaf={index === lastIndex}
            showSeparator={index > 0}
            actions={index === lastIndex ? leafActions : undefined}
          />
        ))}
        {detail === undefined ? null : <GlobalBreadcrumbDetail detail={detail} />}
      </ol>
    </nav>
  );
}

/**
 * The trailing qualifier, and the one segment that yields before the page name.
 *
 * It is not a crumb: it carries no `aria-current`, because "Jul 28 to Aug 26"
 * is not somewhere you navigated to. It is also the first thing to go when the
 * bar runs out of room — `hidden md:flex` drops it entirely on a phone, and
 * `min-w-0 shrink` plus `truncate` let it give up width to the crumbs before it
 * rather than pushing them out. Upstream renders it exactly this way.
 */
function GlobalBreadcrumbDetail({ detail }: { readonly detail: string }): ReactElement {
  return (
    <li className="hidden min-w-0 shrink items-center gap-1.5 text-muted-foreground md:flex">
      <span aria-hidden="true" className="shrink-0 text-muted-foreground/50">
        /
      </span>
      <span className="truncate" data-testid="global-breadcrumb-detail">
        {detail}
      </span>
    </li>
  );
}

function GlobalBreadcrumbItem({
  crumb,
  isLeaf,
  showSeparator,
  actions,
}: {
  readonly crumb: BreadcrumbCrumb;
  readonly isLeaf: boolean;
  readonly showSeparator: boolean;
  readonly actions?: BreadcrumbLeafActions | undefined;
}): ReactElement {
  return (
    <li
      aria-current={isLeaf ? "page" : undefined}
      className={cn(
        "flex min-w-0 items-center gap-1.5",
        // Ancestors are structural context, not the answer — they step aside on
        // touch widths, and above them they give the slack to the leaf rather
        // than take it. Forge names checkouts after their branch, so an ancestor
        // is routinely the longest string in the bar; uncapped and unyielding it
        // left the thread title a few characters wide.
        //
        isLeaf
          ? "flex-1 text-foreground"
          : "hidden max-w-40 shrink-0 text-muted-foreground sm:flex",
      )}
      data-breadcrumb-leaf={isLeaf ? "true" : "false"}
    >
      {showSeparator ? (
        <span aria-hidden="true" className="hidden shrink-0 text-muted-foreground/50 sm:inline">
          /
        </span>
      ) : null}
      {/* Carried over from the project page's own crumb: a project's mark is
          real identity, and it was the one thing the bar could not express. */}
      {crumb.icon !== undefined ? <GlobalBreadcrumbMark icon={crumb.icon} /> : null}
      <GlobalBreadcrumbItemContent crumb={crumb} isLeaf={isLeaf} actions={actions} />
    </li>
  );
}

/**
 * What a crumb actually says: a link, a name, or a name being edited.
 *
 * Split from the item so the item stays about the row — separator, mark,
 * responsive behaviour — and this stays about the one three-way choice.
 * Folding them together put every rename and menu branch inside a component
 * whose job was layout.
 */
function GlobalBreadcrumbItemContent({
  crumb,
  isLeaf,
  actions,
}: {
  readonly crumb: BreadcrumbCrumb;
  readonly isLeaf: boolean;
  readonly actions?: BreadcrumbLeafActions | undefined;
}): ReactElement {
  if (!isLeaf) return <AncestorCrumb crumb={crumb} />;
  if (actions?.rename !== undefined) {
    return <BreadcrumbLeafRenameInput rename={actions.rename} />;
  }

  return actions?.onContextMenu ? (
    <BreadcrumbLeafActionButton label={crumb.label} actions={actions} />
  ) : (
    <span className="truncate font-medium tracking-tight" title={crumb.label}>
      {crumb.label}
    </span>
  );
}

function GlobalBreadcrumbMark({ icon }: { readonly icon: BreadcrumbCrumbIcon }): ReactElement {
  return (
    <ProjectIcon
      className="size-4"
      canonicalIdentity={icon.canonicalIdentity}
      cwd={icon.cwd ?? ""}
      environmentId={(icon.environmentId ?? "") as EnvironmentId}
      projectName={icon.name}
    />
  );
}

function AncestorCrumb({ crumb }: { readonly crumb: BreadcrumbCrumb }) {
  if (crumb.to !== undefined) {
    return (
      <Link
        className="truncate rounded-sm outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        to={crumb.to}
      >
        {crumb.label}
      </Link>
    );
  }
  return <span className="truncate font-medium tracking-tight">{crumb.label}</span>;
}
